'use client';

import { useState } from 'react';
import { CWE } from '../lib/types';
import { CWEHistoricalRanking } from '../data/top25-all-years';
import Link from 'next/link';
import { ArrowLeft, ExternalLink, AlertTriangle, Shield, Bug, BookOpen, Network, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import CWEHero from './CWEHero';
import LikelihoodExplainer from './LikelihoodExplainer';
import CWERelatedResources from './CWERelatedResources';
import { BreadcrumbItem } from '@/components/Breadcrumbs';

interface CWEDetailClientProps {
  cwe: CWE;
  historicalRanking?: CWEHistoricalRanking;
  breadcrumbs?: BreadcrumbItem[];
}

type Tab = 'overview' | 'consequences' | 'mitigations' | 'detection' | 'examples' | 'relationships';

export default function CWEDetailClient({ cwe, historicalRanking, breadcrumbs }: CWEDetailClientProps) {
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  const getLikelihoodColor = (likelihood?: string) => {
    switch (likelihood) {
      case 'High':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'Medium':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'Low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'consequences', label: 'Consequences', icon: <AlertTriangle className="w-4 h-4" /> },
    { id: 'mitigations', label: 'Mitigations', icon: <Shield className="w-4 h-4" /> },
    { id: 'detection', label: 'Detection', icon: <Bug className="w-4 h-4" /> },
    { id: 'examples', label: 'Examples', icon: <ExternalLink className="w-4 h-4" /> },
    { id: 'relationships', label: 'Relationships', icon: <Network className="w-4 h-4" /> },
  ];

  // Determine if this CWE is in the Top 25
  const isTop25 = historicalRanking && historicalRanking.rankings.some(r => r.rank !== null);

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Hero Section */}
      <CWEHero cwe={cwe} historicalRanking={historicalRanking} breadcrumbs={breadcrumbs} />

      {/* Likelihood Explainer */}
      <div className="mb-6">
        <LikelihoodExplainer
          likelihood={cwe.likelihoodOfExploit}
          abstractionLevel={cwe.abstractionLevel}
        />
      </div>

      {/* Back Button */}
      <Link
        href="/tools/security/cwe-lookup"
        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to CWE Lookup
      </Link>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {cwe.extendedDescription && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                    Extended Description
                  </h2>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {cwe.extendedDescription}
                  </p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Technical Details
                  </h3>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="font-medium text-gray-700 dark:text-gray-300">Structure</dt>
                      <dd className="text-gray-600 dark:text-gray-400">{cwe.structure}</dd>
                    </div>
                    {cwe.vulnerabilityMapping && (
                      <div>
                        <dt className="font-medium text-gray-700 dark:text-gray-300">Vulnerability Mapping</dt>
                        <dd className="text-gray-600 dark:text-gray-400">{cwe.vulnerabilityMapping}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                {(cwe.applicableLanguages || cwe.applicablePlatforms) && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                      Applicable To
                    </h3>
                    {cwe.applicableLanguages && (
                      <div className="mb-3">
                        <div className="font-medium text-gray-700 dark:text-gray-300 text-sm mb-1">Languages</div>
                        <div className="flex flex-wrap gap-2">
                          {cwe.applicableLanguages.map((lang) => (
                            <span
                              key={lang}
                              className="px-2 py-1 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 rounded text-xs"
                            >
                              {lang}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {cwe.applicablePlatforms && (
                      <div>
                        <div className="font-medium text-gray-700 dark:text-gray-300 text-sm mb-1">Platforms</div>
                        <div className="flex flex-wrap gap-2">
                          {cwe.applicablePlatforms.map((platform) => (
                            <span
                              key={platform}
                              className="px-2 py-1 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300 rounded text-xs"
                            >
                              {platform}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {historicalRanking && (
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-3 flex items-center gap-2">
                    🏆 CWE Top 25 Historical Ranking
                  </h3>

                  {/* Current Ranking */}
                  <div className="mb-4">
                    {historicalRanking.rankings.filter((r) => r.rank !== null).map((ranking, idx, arr) => {
                      const isLatest = idx === arr.length - 1;
                      return (
                        <div key={ranking.year} className={`flex items-center justify-between py-2 ${isLatest ? 'border-t-2 border-purple-300 dark:border-purple-700 pt-3' : ''}`}>
                          <div className="flex items-center gap-3">
                            <span className={`font-bold ${isLatest ? 'text-lg text-purple-900 dark:text-purple-100' : 'text-sm text-purple-700 dark:text-purple-300'}`}>
                              {ranking.year}:
                            </span>
                            <span className={`font-bold ${isLatest ? 'text-2xl text-purple-900 dark:text-purple-100' : 'text-lg text-purple-700 dark:text-purple-300'}`}>
                              #{ranking.rank}
                            </span>
                            {idx > 0 && arr[idx - 1].rank && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                ranking.rank! < arr[idx - 1].rank!
                                  ? 'bg-green-200 text-green-900 dark:bg-green-900/40 dark:text-green-200'
                                  : ranking.rank! > arr[idx - 1].rank!
                                  ? 'bg-red-200 text-red-900 dark:bg-red-900/40 dark:text-red-200'
                                  : 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-200'
                              }`}>
                                {ranking.rank! < arr[idx - 1].rank! ? `↑${arr[idx - 1].rank! - ranking.rank!}` : ranking.rank! > arr[idx - 1].rank! ? `↓${ranking.rank! - arr[idx - 1].rank!}` : '='}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-purple-600 dark:text-purple-400">Score: {ranking.score.toFixed(2)}</div>
                            <div className="text-xs text-purple-600 dark:text-purple-400">{ranking.cveCount.toLocaleString()} CVEs</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Trend Summary */}
                  <div className="flex items-center gap-2 text-sm pt-2 border-t border-purple-200 dark:border-purple-700">
                    <span className="text-purple-700 dark:text-purple-300 font-medium">Trend:</span>
                    {historicalRanking.trend === 'rising' && (
                      <span className="flex items-center gap-1 text-green-700 dark:text-green-300 font-semibold">
                        <TrendingUp className="w-4 h-4" />
                        Improving (moved up {Math.abs(historicalRanking.rankChange || 0)} ranks)
                      </span>
                    )}
                    {historicalRanking.trend === 'falling' && (
                      <span className="flex items-center gap-1 text-red-700 dark:text-red-300 font-semibold">
                        <TrendingDown className="w-4 h-4" />
                        Worsening (moved down {historicalRanking.rankChange} ranks)
                      </span>
                    )}
                    {historicalRanking.trend === 'stable' && (
                      <span className="flex items-center gap-1 text-gray-700 dark:text-gray-300 font-semibold">
                        <Minus className="w-4 h-4" />
                        Stable (no rank change)
                      </span>
                    )}
                    {historicalRanking.trend === 'new' && (
                      <span className="flex items-center gap-1 text-blue-700 dark:text-blue-300 font-semibold">
                        ★ New to Top 25
                      </span>
                    )}
                    {historicalRanking.trend === 'dropped' && (
                      <span className="flex items-center gap-1 text-orange-700 dark:text-orange-300 font-semibold">
                        ✗ Dropped from Top 25
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Consequences Tab */}
          {activeTab === 'consequences' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Security Consequences
              </h2>
              {cwe.consequences && cwe.consequences.length > 0 ? (
                <div className="space-y-4">
                  {cwe.consequences.map((consequence, idx) => (
                    <div
                      key={idx}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Scope</h4>
                          <div className="flex flex-wrap gap-1">
                            {consequence.scope.map((s) => (
                              <span key={s} className="px-2 py-1 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 rounded text-xs">
                                {s}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Impact</h4>
                          <div className="flex flex-wrap gap-1">
                            {consequence.impact.map((i) => (
                              <span key={i} className="px-2 py-1 bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300 rounded text-xs">
                                {i}
                              </span>
                            ))}
                          </div>
                        </div>
                        {consequence.likelihood && (
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Likelihood</h4>
                            <span className={`px-2 py-1 rounded text-xs ${getLikelihoodColor(consequence.likelihood)}`}>
                              {consequence.likelihood}
                            </span>
                          </div>
                        )}
                      </div>
                      {consequence.note && (
                        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">{consequence.note}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">No consequence information available for this CWE.</p>
              )}
            </div>
          )}

          {/* Mitigations Tab */}
          {activeTab === 'mitigations' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Mitigation Strategies
              </h2>
              {cwe.mitigations && cwe.mitigations.length > 0 ? (
                <div className="space-y-4">
                  {cwe.mitigations.map((mitigation, idx) => (
                    <div
                      key={idx}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                      <div className="mb-3">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Phase</h4>
                        <div className="flex flex-wrap gap-1">
                          {mitigation.phase.map((p) => (
                            <span key={p} className="px-2 py-1 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 rounded text-xs">
                              {p}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="mb-3">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Description</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{mitigation.description}</p>
                      </div>
                      {mitigation.strategy && (
                        <div className="mb-3">
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Strategy</h4>
                          <span className="px-2 py-1 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300 rounded text-xs">
                            {mitigation.strategy}
                          </span>
                        </div>
                      )}
                      {mitigation.effectiveness && (
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Effectiveness</h4>
                          <span className="px-2 py-1 bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 rounded text-xs">
                            {mitigation.effectiveness}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">No mitigation information available for this CWE.</p>
              )}
            </div>
          )}

          {/* Detection Tab */}
          {activeTab === 'detection' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Detection Methods
              </h2>
              {cwe.detectionMethods && cwe.detectionMethods.length > 0 ? (
                <div className="space-y-4">
                  {cwe.detectionMethods.map((method, idx) => (
                    <div
                      key={idx}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                      <div className="mb-3">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Method</h4>
                        <span className="px-2 py-1 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300 rounded text-xs">
                          {method.method}
                        </span>
                      </div>
                      {method.description && (
                        <div className="mb-3">
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Description</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{method.description}</p>
                        </div>
                      )}
                      {method.effectiveness && (
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Effectiveness</h4>
                          <span className="px-2 py-1 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300 rounded text-xs">
                            {method.effectiveness}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">No detection method information available for this CWE.</p>
              )}
            </div>
          )}

          {/* Examples Tab */}
          {activeTab === 'examples' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Code Examples & CVEs
              </h2>

              {/* Demonstrative Examples */}
              {cwe.demonstrativeExamples && cwe.demonstrativeExamples.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Demonstrative Examples</h3>
                  <div className="space-y-4">
                    {cwe.demonstrativeExamples.map((example, idx) => (
                      <div
                        key={idx}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                      >
                        {example.introText && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{example.introText}</p>
                        )}
                        {example.bodyText && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{example.bodyText}</p>
                        )}
                        {example.exampleCode && example.exampleCode.length > 0 && (
                          <div className="space-y-2">
                            {example.exampleCode.map((code, codeIdx) => (
                              <div key={codeIdx} className="bg-gray-50 dark:bg-gray-900 rounded p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    code.nature === 'Bad' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                                    code.nature === 'Good' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                                    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                                  }`}>
                                    {code.nature}
                                  </span>
                                  <span className="px-2 py-1 bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded text-xs">
                                    {code.language}
                                  </span>
                                </div>
                                <pre className="text-xs overflow-x-auto"><code>{code.code}</code></pre>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Observed CVE Examples */}
              {cwe.observedExamples && cwe.observedExamples.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Observed CVE Examples ({cwe.observedExamples.length})
                  </h3>
                  <div className="space-y-2">
                    {cwe.observedExamples.map((observed, idx) => (
                      <div
                        key={idx}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                      >
                        <div className="flex items-start gap-3">
                          <Link
                            href={`/tools/security/cve-lookup/${observed.reference.toLowerCase()}`}
                            className="px-2 py-1 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-xs font-mono flex-shrink-0 transition-colors"
                          >
                            {observed.reference}
                          </Link>
                          <p className="text-sm text-gray-600 dark:text-gray-400 flex-1">{observed.description}</p>
                          <Link
                            href={`/tools/security/cve-lookup/${observed.reference.toLowerCase()}`}
                            className="text-blue-600 dark:text-blue-400 hover:underline text-xs flex-shrink-0"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!cwe.demonstrativeExamples || cwe.demonstrativeExamples.length === 0) &&
               (!cwe.observedExamples || cwe.observedExamples.length === 0) && (
                <p className="text-gray-600 dark:text-gray-400">No examples or observed CVEs available for this CWE.</p>
              )}
            </div>
          )}

          {/* Relationships Tab */}
          {activeTab === 'relationships' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                CWE Relationships
              </h2>

              {/* Parent CWEs */}
              {cwe.parentCWEs && cwe.parentCWEs.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Parent Weaknesses
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    This CWE is a child of the following more abstract weakness classes:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {cwe.parentCWEs.map((parentId) => (
                      <Link
                        key={parentId}
                        href={`/tools/security/cwe-lookup/cwe-${parentId}`}
                        className="px-3 py-2 bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 rounded border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors text-sm font-medium"
                      >
                        CWE-{parentId}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Child CWEs */}
              {cwe.childCWEs && cwe.childCWEs.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Child Weaknesses
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    The following more specific weaknesses are children of this CWE:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {cwe.childCWEs.map((childId) => (
                      <Link
                        key={childId}
                        href={`/tools/security/cwe-lookup/cwe-${childId}`}
                        className="px-3 py-2 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 rounded border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-sm font-medium"
                      >
                        CWE-{childId}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* No relationships message */}
              {(!cwe.parentCWEs || cwe.parentCWEs.length === 0) &&
               (!cwe.childCWEs || cwe.childCWEs.length === 0) && (
                <p className="text-gray-600 dark:text-gray-400">
                  No relationship information available for this CWE.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Related Resources */}
      <CWERelatedResources
        cweId={cwe.cweId}
        cweNumericId={cwe.id}
        isTop25={isTop25}
      />
    </div>
  );
}
